import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import './InspirationCanvas.css';
import ApiService from '../../api/ApiService';
import LogService from '../../api/LogService';
import { useAuth } from '../../components/auth/AuthContext';
import juchachaLogo from '../../assets/juchacha-super-factory-logo.png';
import { toast } from 'react-toastify';

const apiService = new ApiService();

const InspirationCanvas = () => {
  const location = useLocation();
  const history = useHistory();
  const { user: userInfo, openLogin } = useAuth();
  
  // 从 URL 查询参数读取初始 tab
  const getInitialTab = () => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    return tabParam === 'mine' ? 'mine' : 'discovery';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab); // 'discovery' | 'mine'
  
  // 当 tab 改变时，清除 URL 中的 tab 参数（避免刷新时保留）
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('tab')) {
      searchParams.delete('tab');
      const newSearch = searchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : ''
      });
    }
  }, []);
  
  // Discovery projects state
  const [discoveryProjects, setDiscoveryProjects] = useState([]);
  const [discoveryPage, setDiscoveryPage] = useState(1);
  const [discoveryHasMore, setDiscoveryHasMore] = useState(true);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  // My projects state
  const [mineProjects, setMineProjects] = useState([]);
  const [minePage, setMinePage] = useState(1);
  const [mineHasMore, setMineHasMore] = useState(true);
  const [mineLoading, setMineLoading] = useState(false);

  // Masonry columns state
  const [numColumns, setNumColumns] = useState(4);

  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const defaultUpgradeMessage = '普通会员最多只能创建1个项目（包括克隆），请升级会员等级';

  const observer = useRef();
  // 移除 lastProjectElementRef，改用在每个列表末尾添加一个哨兵元素来触发加载

  // 监听窗口大小变化调整列数
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1920) setNumColumns(6);
      else if (width >= 1440) setNumColumns(5);
      else if (width >= 1024) setNumColumns(4);
      else if (width >= 640) setNumColumns(3);
      else setNumColumns(2);
    };
    
    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const extractErrorMessage = (err, defaultMsg = '操作失败') => {
    if (!err) return defaultMsg;
    if (typeof err === 'string') return err;

    if (err.data) {
      if (typeof err.data === 'string') {
        try {
          const parsed = JSON.parse(err.data);
          if (parsed) {
            if (Array.isArray(parsed.detail)) {
              return parsed.detail.join('; ');
            }
            if (parsed.detail) {
              return parsed.detail;
            }
            if (parsed.message) {
              return parsed.message;
            }
            if (parsed.msg) {
              return parsed.msg;
            }
          }
        } catch (parseError) {
          if (err.data.trim()) {
            return err.data;
          }
        }
      } else if (typeof err.data === 'object' && err.data !== null) {
        if (Array.isArray(err.data.detail)) {
          return err.data.detail.join('; ');
        }
        return err.data.detail || err.data.message || err.data.msg || defaultMsg;
      }
    }

    if (err.message && typeof err.message === 'string') {
      return err.message;
    }

    return defaultMsg;
  };
  
  // 创建/编辑项目对话框状态
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    is_public: false
  });
  
  // 删除确认对话框状态
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingProject, setDeletingProject] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 初始化公开灵感内容
  useEffect(() => {
    // 记录页面访问日志
    LogService.logPageView('灵感画布', {
      pageCategory: '创作工具',
      pageType: '项目管理页面',
      pageFeatures: ['项目列表', '创建项目', '推荐案例']
    });
    
    // 初始加载 Discovery
    loadProjects('discovery', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 切换 Tab 时加载数据（如果是第一次切到这个Tab）
  useEffect(() => {
    if (activeTab === 'mine' && mineProjects.length === 0 && userInfo) {
      loadProjects('mine', 1);
    }
  }, [activeTab, userInfo]);

  // 加载项目列表
  const loadProjects = async (type, page) => {
    const limit = 40;
    const skip = (page - 1) * limit;
    
    try {
      setError(null);
      
      if (type === 'discovery') {
        setDiscoveryLoading(true);
        const data = await apiService.getPublicDrawProjects(skip, limit);
        const newProjects = data.projects || [];
        
        if (page === 1) {
          setDiscoveryProjects(newProjects);
        } else {
          setDiscoveryProjects(prev => {
            // 去重，防止key重复
            const existingIds = new Set(prev.map(p => p.id || p.uuid));
            const uniqueNewProjects = newProjects.filter(p => !existingIds.has(p.id || p.uuid));
            return [...prev, ...uniqueNewProjects];
          });
        }
        
        setDiscoveryHasMore(newProjects.length === limit);
        setDiscoveryPage(page);
      } else {
        // Mine
        if (!userInfo?.token) {
          setMineLoading(false);
          return;
        }
        
        setMineLoading(true);
        const data = await apiService.getMyDrawProjects(userInfo.token, skip, limit);
        const newProjects = data.projects || [];
        
        if (page === 1) {
          setMineProjects(newProjects);
        } else {
          setMineProjects(prev => {
             const existingIds = new Set(prev.map(p => p.id || p.uuid));
             const uniqueNewProjects = newProjects.filter(p => !existingIds.has(p.id || p.uuid));
             return [...prev, ...uniqueNewProjects];
          });
        }
        
        setMineHasMore(newProjects.length === limit);
        setMinePage(page);
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '加载项目列表失败');
      setError(errorMsg);
      console.error(`加载${type}项目失败:`, err);
    } finally {
      if (type === 'discovery') setDiscoveryLoading(false);
      else setMineLoading(false);
    }
  };

  // 刷新当前 Tab 数据
  const refreshCurrentTab = async () => {
    if (activeTab === 'discovery') {
      setDiscoveryPage(1);
      setDiscoveryHasMore(true);
      setDiscoveryProjects([]);
      await loadProjects('discovery', 1);
    } else {
      setMinePage(1);
      setMineHasMore(true);
      setMineProjects([]);
      await loadProjects('mine', 1);
    }
  };

  // 生成随机项目名称
  const generateRandomProjectName = () => {
    const adjectives = ['创意', '智能', '未来', '梦想', '灵感', '神奇', '精彩', '炫酷', '极致', '超凡'];
    const nouns = ['画布', '项目', '创作', '空间', '作品', '设计', '方案'];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const timestamp = new Date().getTime().toString().slice(-4);
    return `${randomAdjective}${randomNoun}_${timestamp}`;
  };

  const getProjectIdentifier = (project) => {
    if (!project) {
      return null;
    }
    return project.uuid || project.id || null;
  };

  // 快速创建项目并打开
  const handleCreateClick = async () => {
    // 记录按钮点击日志
    LogService.logButtonClick('立即创建', 'create-project-button', {
      actionType: '创建新项目',
      pageName: '灵感画布'
    });
    
    try {
      if (!userInfo || !userInfo.token) {
        openLogin();
        return;
      }
      
      const randomName = generateRandomProjectName();
      const newProject = await apiService.createDrawProject(userInfo.token, randomName, '', false);
      
      // 记录项目创建成功事件
      LogService.logCustomEvent('project_created', {
        projectName: randomName,
        actionResult: 'success',
        pageName: '灵感画布'
      });
      
      toast.success('项目创建成功');
      // 刷新我的项目列表
      if (activeTab === 'mine') {
        await refreshCurrentTab();
      } else {
        // 如果在发现页创建，切换到我的页面并刷新
        setActiveTab('mine');
        // useEffect 会触发加载，这里不需要手动 refresh
      }
      
      // 跳转到画布编辑器
      const projectIdentifier = getProjectIdentifier(newProject);
      if (!projectIdentifier) {
        toast.error('新项目缺少唯一标识，无法打开');
        return;
      }

      // 创建的项目属于"我的"，添加来源参数以便返回时回到我的卡片
      history.push(`/canvas-editor/${projectIdentifier}?from=my-canvas`);
      console.log('创建的项目:', newProject);
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '创建项目失败');
      const normalizedMessage = (errorMsg || '').trim();
      
      // 记录项目创建失败事件
      LogService.logCustomEvent('project_create_failed', {
        errorMessage: normalizedMessage,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      if (normalizedMessage.includes('升级会员') || normalizedMessage.includes('普通会员')) {
        const cleanedMessage = normalizedMessage.replace(/^创建项目失败[:：]?\s*/, '');
        setUpgradeMessage(cleanedMessage || defaultUpgradeMessage);
        setShowUpgradeModal(true);
      } else {
        toast.error(normalizedMessage || '创建项目失败');
      }
      console.error('创建项目失败:', err);
    }
  };

  // 打开编辑对话框
  const handleEditClick = (project) => {
    // 记录编辑按钮点击日志
    LogService.logButtonClick('编辑项目', 'edit-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      actionType: '打开编辑对话框',
      pageName: '灵感画布'
    });
    
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      description: project.description || '',
      is_public: project.is_public
    });
    setShowDialog(true);
  };

  // 保存项目（更新）
  const handleSave = async () => {
    // 记录保存按钮点击日志
    LogService.logButtonClick('保存项目', 'save-project-button', {
      projectName: projectForm.name,
      isPublic: projectForm.is_public,
      actionType: '保存项目信息',
      pageName: '灵感画布'
    });
    
    try {
      if (!userInfo || !userInfo.token) {
        openLogin();
        return;
      }
      
      if (!projectForm.name.trim()) {
        toast.error('请输入项目名称');
        return;
      }

      if (editingProject) {
        const projectIdentifier = getProjectIdentifier(editingProject);
        if (!projectIdentifier) {
          toast.error('项目缺少唯一标识，无法保存');
          return;
        }

        await apiService.updateDrawProject(userInfo.token, projectIdentifier, projectForm);
        
        // 记录项目更新成功事件
        LogService.logCustomEvent('project_updated', {
          projectId: projectIdentifier,
          projectName: projectForm.name,
          isPublic: projectForm.is_public,
          actionResult: 'success',
          pageName: '灵感画布'
        });
        
        setShowDialog(false);
        await refreshCurrentTab();
        toast.success('项目已保存');
      }
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '保存项目失败');
      
      // 记录项目更新失败事件
      LogService.logCustomEvent('project_update_failed', {
        errorMessage: errorMsg,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      toast.error(errorMsg);
      console.error('保存项目失败:', err);
    }
  };

  // 打开删除确认对话框
  const handleDeleteClick = (project) => {
    // 记录删除按钮点击日志
    LogService.logButtonClick('删除项目', 'delete-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      actionType: '打开删除确认对话框',
      pageName: '灵感画布'
    });
    
    setDeletingProject(project);
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  // 确认删除项目
  const handleConfirmDelete = async () => {
    if (!deletingProject) return;
    
    if (!userInfo || !userInfo.token) {
      openLogin();
      return;
    }

    // 记录确认删除按钮点击日志
    LogService.logButtonClick('确认删除', 'confirm-delete-button', {
      projectName: deletingProject.name,
      projectId: getProjectIdentifier(deletingProject),
      actionType: '确认删除项目',
      pageName: '灵感画布'
    });

    try {
      setIsDeleting(true);
      setDeleteError(null);
      const projectIdentifier = getProjectIdentifier(deletingProject);
      if (!projectIdentifier) {
        toast.error('项目缺少唯一标识，无法删除');
        return;
      }

      await apiService.deleteDrawProject(userInfo.token, projectIdentifier);
      
      // 记录项目删除成功事件
      LogService.logCustomEvent('project_deleted', {
        projectId: projectIdentifier,
        projectName: deletingProject.name,
        actionResult: 'success',
        pageName: '灵感画布'
      });
      
      setShowDeleteDialog(false);
      setDeletingProject(null);
      
      // 本地移除，不重新请求接口
      if (activeTab === 'mine') {
        setMineProjects(prev => prev.filter(p => getProjectIdentifier(p) !== projectIdentifier));
      }
      toast.success('项目已删除');
    } catch (err) {
      const errorMsg = extractErrorMessage(err, '删除项目失败');
      
      // 记录项目删除失败事件
      LogService.logCustomEvent('project_delete_failed', {
        projectId: getProjectIdentifier(deletingProject),
        errorMessage: errorMsg,
        actionResult: 'error',
        pageName: '灵感画布'
      });
      
      setDeleteError(errorMsg);
      console.error('删除项目失败:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // 取消删除
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeletingProject(null);
    setDeleteError(null);
  };

  // 打开项目
  const handleOpenProject = async (project) => {
    // 记录打开项目日志
    LogService.logButtonClick('打开项目', 'open-project-button', {
      projectName: project.name,
      projectId: getProjectIdentifier(project),
      isPublic: project.is_public || false,
      actionType: '打开画布编辑器',
      pageName: '灵感画布'
    });
    
    // 直接跳转到画布编辑器（不再自动克隆）
    const projectIdentifier = getProjectIdentifier(project);
    if (!projectIdentifier) {
      toast.error('项目缺少唯一标识，无法打开');
      return;
    }

    // 根据当前 tab 添加来源参数，以便返回时能回到正确的页面
    const fromParam = activeTab === 'mine' ? '?from=my-canvas' : '';
    history.push(`/canvas-editor/${projectIdentifier}${fromParam}`);
    console.log('打开项目:', project);
  };

  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
  };

  const handleGoToMembership = () => {
    // 记录去开通会员按钮点击日志
    LogService.logButtonClick('去开通会员', 'go-to-membership-button', {
      fromDialog: '升级会员提示',
      actionType: '跳转到会员充值页',
      pageName: '灵感画布'
    });
    
    setShowUpgradeModal(false);
    window.open('https://apiz.ai/#/recharge', '_blank', 'noopener,noreferrer');
  };

  // 将项目分配到列中 - 纯 JS 实现瀑布流
  const masonryColumns = useMemo(() => {
    const columns = Array.from({ length: numColumns }, () => []);
    
    // 如果是我的项目，将"创建"卡片作为第一个元素处理
    let itemsToDistribute = [];
    if (activeTab === 'mine' && userInfo?.token) {
      itemsToDistribute = [{ isCreate: true }, ...mineProjects];
    } else {
      itemsToDistribute = discoveryProjects;
    }

    itemsToDistribute.forEach((item, index) => {
      const columnIndex = index % numColumns;
      columns[columnIndex].push(item);
    });

    return columns;
  }, [activeTab, discoveryProjects, mineProjects, numColumns, userInfo?.token]);

  // 哨兵元素 Ref
  const sentinelRef = useCallback(node => {
    const loading = activeTab === 'discovery' ? discoveryLoading : mineLoading;
    const hasMore = activeTab === 'discovery' ? discoveryHasMore : mineHasMore;

    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        const nextPage = activeTab === 'discovery' ? discoveryPage + 1 : minePage + 1;
        loadProjects(activeTab, nextPage);
      }
    });
    if (node) observer.current.observe(node);
  }, [activeTab, discoveryLoading, mineLoading, discoveryHasMore, mineHasMore, discoveryPage, minePage]);

  return (
    <div className="InspirationCanvas-container">
      {/* 独立项目标题与视图切换 */}
      <header className="InspirationCanvas-header">
        <div className="InspirationCanvas-header-inner">
          <div className="InspirationCanvas-heading">
            <img
              className="InspirationCanvas-brand-logo"
              src={juchachaLogo}
              alt="剧查查超级工厂"
            />
            <h1>欢迎进入灵感画布</h1>
            <div className="InspirationCanvas-announcement">
              <span aria-hidden="true" />
              灵感画布已独立运行，项目与线上账户实时同步
            </div>
          </div>
          <div className="InspirationCanvas-tabs">
            <button
              type="button"
              className={`InspirationCanvas-tab ${activeTab === 'discovery' ? 'active' : ''}`}
              onClick={() => setActiveTab('discovery')}
            >
              灵感广场 <span>{discoveryProjects.length || '·'}</span>
            </button>
            <button
              type="button"
              className={`InspirationCanvas-tab ${activeTab === 'mine' ? 'active' : ''}`}
              onClick={() => setActiveTab('mine')}
            >
              灵感画布 <span>{mineProjects.length || '·'}</span>
            </button>
          </div>
        </div>
      </header>
      
      {/* 内容区域 - JS 瀑布流布局 */}
      <main className="InspirationCanvas-main">
        <div className="InspirationCanvas-section-header">
          <div>
            <span>{activeTab === 'mine' ? 'YOUR WORKSPACE' : 'COMMUNITY PICKS'}</span>
            <h2>{activeTab === 'mine' ? '我的项目' : '发现灵感'}</h2>
          </div>
          <p>{activeTab === 'mine' ? '创建或继续编辑你的画布项目' : '从公开作品中找到下一次创作的起点'}</p>
        </div>

        {error && (
          <div className="InspirationCanvas-error">
            <p>{error}</p>
            <button onClick={refreshCurrentTab}>重试</button>
          </div>
        )}

        {activeTab === 'mine' && !userInfo?.token ? (
          <section className="InspirationCanvas-login-prompt" aria-label="登录后使用灵感画布">
            <span className="InspirationCanvas-login-prompt-mark" aria-hidden="true">剧</span>
            <div>
              <h3>登录后创建你的灵感画布</h3>
              <p>公开作品可以直接浏览，登录后即可创建、保存并管理个人项目。</p>
            </div>
            <button type="button" onClick={() => openLogin()}>登录 / 注册</button>
          </section>
        ) : <div className="InspirationCanvas-masonry-container">
          {masonryColumns.map((columnItems, colIndex) => (
            <div key={colIndex} className="InspirationCanvas-masonry-column">
              {columnItems.map((item, itemIndex) => {
                // 特殊处理"创建"卡片
                if (item.isCreate) {
                  return (
                    <div 
                      key="create-card"
                      className="InspirationCanvas-masonry-item"
                      onClick={handleCreateClick}
                    >
                      <div className="InspirationCanvas-create-card">
                        <div className="InspirationCanvas-create-content">
                          <div className="InspirationCanvas-create-icon">
                            <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                              <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                              <path d="M32 20V44M20 32H44" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                            </svg>
                          </div>
                          <h3 className="InspirationCanvas-create-text">立即创建</h3>
                        </div>
                      </div>
                    </div>
                  );
                }

                // 普通项目卡片
                const project = item;
                
                return (
                  <div 
                    key={getProjectIdentifier(project) || `${activeTab}-${project.name}-${colIndex}-${itemIndex}`}
                    className="InspirationCanvas-masonry-item"
                    onClick={() => handleOpenProject(project)}
                  >
                    <div className="InspirationCanvas-card-content">
                      {/* 只有"我的" Tab 显示操作按钮 */}
                      {activeTab === 'mine' && (
                        <div className="InspirationCanvas-card-actions">
                          <button 
                            className="InspirationCanvas-action-btn InspirationCanvas-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(project);
                            }}
                            title="编辑"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.333 2A1.886 1.886 0 0 1 14 4.667l-9 9-3.667 1 1-3.667 9-9z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button 
                            className="InspirationCanvas-action-btn InspirationCanvas-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(project);
                            }}
                            title="删除"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      )}

                      <div className="InspirationCanvas-card-thumbnail">
                        {project.thumbnail_url ? (
                          <img src={project.thumbnail_url} alt={project.name} />
                        ) : (
                          <div className="InspirationCanvas-card-placeholder">
                            <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                              <rect x="10" y="15" width="40" height="30" rx="2" stroke="currentColor" strokeWidth="2"/>
                              <circle cx="22" cy="25" r="3" fill="currentColor"/>
                              <path d="M10 35 L20 28 L30 35 L45 22 L50 27" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </div>
                        )}
                      </div>

                      <div className="InspirationCanvas-card-info">
                        <h3 className="InspirationCanvas-card-title">
                          {project.name}
                        </h3>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>}

        {/* 加载更多触发器 - 哨兵元素 */}
        <div ref={sentinelRef} style={{ height: '20px', width: '100%' }}></div>

        {/* 状态提示 */}
        {((activeTab === 'discovery' && discoveryLoading) || (activeTab === 'mine' && mineLoading)) && (
          <div className="InspirationCanvas-loading">加载中...</div>
        )}
        
        {((activeTab === 'discovery' && !discoveryHasMore && discoveryProjects.length > 0) || 
          (activeTab === 'mine' && !mineHasMore && mineProjects.length > 0)) && (
          <div className="InspirationCanvas-no-more">没有更多了</div>
        )}
        
        {((activeTab === 'discovery' && !discoveryLoading && discoveryProjects.length === 0) || 
          (activeTab === 'mine' && userInfo?.token && !mineLoading && mineProjects.length === 0)) && (
          <div className="InspirationCanvas-empty-message">
            <p>暂无内容</p>
          </div>
        )}
      </main>

      {/* 编辑项目对话框 */}
      {showDialog && (
        <div className="InspirationCanvas-dialog-overlay" onClick={() => setShowDialog(false)}>
          <div 
            className="InspirationCanvas-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="InspirationCanvas-dialog-title">
              编辑项目
            </h2>

            <div className="InspirationCanvas-form">
              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-label">项目名称 *</label>
                <input
                  type="text"
                  className="InspirationCanvas-form-input"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="输入项目名称"
                  autoFocus
                />
              </div>

              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-label">项目描述</label>
                <textarea
                  className="InspirationCanvas-form-textarea"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="输入项目描述（可选）"
                  rows={3}
                />
              </div>

              <div className="InspirationCanvas-form-group">
                <label className="InspirationCanvas-form-checkbox">
                  <input
                    type="checkbox"
                    checked={projectForm.is_public}
                    onChange={(e) => setProjectForm({ ...projectForm, is_public: e.target.checked })}
                  />
                  <span>公开项目（其他用户可以查看）</span>
                </label>
              </div>

              <div className="InspirationCanvas-dialog-actions">
                <button
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                  onClick={() => setShowDialog(false)}
                >
                  取消
                </button>
                <button
                  className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-save"
                  onClick={handleSave}
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {showDeleteDialog && deletingProject && (
        <div className="InspirationCanvas-dialog-overlay" onClick={handleCancelDelete}>
          <div 
            className="InspirationCanvas-dialog InspirationCanvas-delete-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="InspirationCanvas-delete-icon">
              <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                <circle cx="28" cy="28" r="28" fill="#FEE2E2"/>
                <path d="M28 20V30M28 34V36" stroke="#DC2626" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>

            <h2 className="InspirationCanvas-dialog-title InspirationCanvas-delete-title">
              确认删除项目
            </h2>

            <p className="InspirationCanvas-delete-message">
              确定要删除项目 <strong>"{deletingProject.name}"</strong> 吗？
            </p>
            
            <p className="InspirationCanvas-delete-warning">
              ⚠️ 此操作无法撤销，项目的所有数据将被永久删除。
            </p>

            {deleteError && (
              <div className="InspirationCanvas-delete-error">
                {deleteError}
              </div>
            )}

            <div className="InspirationCanvas-dialog-actions">
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                取消
              </button>
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpgradeModal && (
        <div className="InspirationCanvas-dialog-overlay InspirationCanvas-upgrade-overlay" onClick={closeUpgradeModal}>
          <div
            className="InspirationCanvas-dialog InspirationCanvas-upgrade-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="InspirationCanvas-upgrade-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L14.39 8.26L21 9.27L16 13.97L17.18 20.54L12 17.27L6.82 20.54L8 13.97L3 9.27L9.61 8.26L12 2Z" fill="currentColor" />
              </svg>
            </div>
            <h2 className="InspirationCanvas-dialog-title InspirationCanvas-upgrade-title">升级会员解锁更多创作空间</h2>
            <p className="InspirationCanvas-upgrade-message">
              {upgradeMessage || defaultUpgradeMessage}
            </p>
            <ul className="InspirationCanvas-upgrade-benefits">
              <li>非会员：最多创建1个项目</li>
              <li>VIP最多创建10个项目</li>
              <li>SVIP项目创建数量无限制</li>
            </ul>
            <div className="InspirationCanvas-dialog-actions InspirationCanvas-upgrade-actions">
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-dialog-button-cancel"
                onClick={closeUpgradeModal}
              >
                稍后再说
              </button>
              <button
                className="InspirationCanvas-dialog-button InspirationCanvas-upgrade-button"
                onClick={handleGoToMembership}
              >
                去开通会员
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspirationCanvas;
